import React from "react";
import BlockPropertiesInfo from "./BlockPropertiesInfo";
import BlockPropertiesSize from "./BlockPropertiesSize";
import BackgroundProperties from "../../../components/BackgroundProperties";
import BlockPropertiesAnimation from "./BlockPropertiesAnimation";
import BlockPropertiesStateAnimation from "./BlockPropertiesStateAnimation";
import BlockPropertiesActions from "./BlockPropertiesActions";
import StateProperties from "../../../components/StateProperties";
import AppConstants from "../../../constants/AppConstants";
import IDUtils from "../../../utils/IDUtils";
import BlockUtils from "../../../utils/BlockUtils";
import BlockUpdateVO from "../../../vo/BlockUpdate";
import DocumentStore from "../../../document/DocumentStore";
import { COMMANDS } from "../../../document/commands";
import { batchPropChanges } from "../../../components/utils/PropsUpdater";
import SplitMergeWrapper from "../../base/SplitMergeWrapper";
import When from "../../../components/utils/When";
import UIActions from "../../../actions/UIActions";
import PropertyPanelSectionWrapper from "../../base/PropertyPanelSectionWrapper";
import AdvancedProperties from "../shared/advanced/AdvancedProperties";

const {
  PANEL_SECTION_BLOCK_INFO,
  PANEL_SECTION_BLOCK_BG,
  PANEL_SECTION_BLOCK_ANIMATION,
  PANEL_SECTION_BLOCK_STATE_ANIMATION,
  PANEL_SECTION_BLOCK_ACTIONS,
  PANEL_SECTION_BLOCK_STATE,
  PANEL_SECTION_BLOCK_ADVANCED,
} = AppConstants;

const BlockProperties = ({ block, layout, viewState, isWP }) => {
  const usingSiteBlock = BlockUtils.isUsingSiteBlock(block);
  const isSiteBlock = BlockUtils.isSiteBlock(block);
  const selectedState = block.getIn(["selected", "blockState"]);
  const state =
    selectedState && block.getIn(IDUtils.getArray(selectedState, 1));
  const wpFeaturedAllowed =
    block.getIn(["wp", "type"], false) === AppConstants.BLOCK_WP_POST;
  const canvasSetId = block.get("canvasSet");

  const _onPropUpdate = (propInfo) => {
    const blockId = block.get("id");
    const update = new BlockUpdateVO();
    const updaterCB = ({
      action = null,
      name,
      value,
      addUndo = false,
      source,
    }) => {
      const pathParts = name.split(".");
      update.add(blockId, pathParts, value, action, addUndo, source);
    };

    batchPropChanges(propInfo, updaterCB);
    DocumentStore.dispatch({ type: COMMANDS.UPDATE_BLOCK, payload: update });
  };

  const _togglePanelSection = (sectionName) => {
    UIActions.toggleBlockPropertiesSection({ name: sectionName });
  };

  const activeSection = viewState.get("activeSection");

  return (
    <>
      {selectedState && (
        <PropertyPanelSectionWrapper
          title="Canvas View"
          open={activeSection === PANEL_SECTION_BLOCK_STATE}
          onSIPanelToggle={() => _togglePanelSection(PANEL_SECTION_BLOCK_STATE)}
        >
          <StateProperties
            block={block}
            stateId={selectedState}
            state={state}
            layout={layout}
          />
        </PropertyPanelSectionWrapper>
      )}
      <PropertyPanelSectionWrapper
        title="Canvas"
        open={activeSection === PANEL_SECTION_BLOCK_INFO}
        onSIPanelToggle={() => _togglePanelSection(PANEL_SECTION_BLOCK_INFO)}
      >
        <BlockPropertiesInfo
          block={block}
          blockId={block.get("id")}
          name={block.get("name")}
          slug={block.get("slug")}
          isWP={isWP}
          onPropUpdate={_onPropUpdate}
        />
        <SplitMergeWrapper layout={layout}>
          <BlockPropertiesSize
            layout={layout}
            block={block}
            usingSiteBlock={usingSiteBlock}
            onPropUpdate={_onPropUpdate}
          />
        </SplitMergeWrapper>
      </PropertyPanelSectionWrapper>
      {!usingSiteBlock && (
        <>
          <PropertyPanelSectionWrapper
            title="Canvas Background"
            data-testid="canvas-background-section"
            open={activeSection === PANEL_SECTION_BLOCK_BG}
            onSIPanelToggle={() => _togglePanelSection(PANEL_SECTION_BLOCK_BG)}
          >
            <BackgroundProperties
              wpFeaturedAllowed={wpFeaturedAllowed}
              videoAllowed
              parent={block}
              layout={layout}
              onPropUpdate={_onPropUpdate}
            />
          </PropertyPanelSectionWrapper>
          <PropertyPanelSectionWrapper
            title="Animation"
            open={activeSection === PANEL_SECTION_BLOCK_ANIMATION}
            onSIPanelToggle={() =>
              _togglePanelSection(PANEL_SECTION_BLOCK_ANIMATION)
            }
          >
            <BlockPropertiesAnimation
              block={block}
              layout={layout}
              onPropUpdate={_onPropUpdate}
            />
          </PropertyPanelSectionWrapper>
          <PropertyPanelSectionWrapper
            title="View Transitions"
            open={activeSection === PANEL_SECTION_BLOCK_STATE_ANIMATION}
            onSIPanelToggle={() =>
              _togglePanelSection(PANEL_SECTION_BLOCK_STATE_ANIMATION)
            }
          >
            <BlockPropertiesStateAnimation
              block={block}
              layout={layout}
              onPropUpdate={_onPropUpdate}
            />
          </PropertyPanelSectionWrapper>
        </>
      )}
      <When it={!canvasSetId && !isSiteBlock}>
        <PropertyPanelSectionWrapper
          title="Scroll Actions"
          open={activeSection === PANEL_SECTION_BLOCK_ACTIONS}
          onSIPanelToggle={() =>
            _togglePanelSection(PANEL_SECTION_BLOCK_ACTIONS)
          }
        >
          <BlockPropertiesActions
            block={block}
            blockId={block.get("id")}
            name={block.get("name")}
            slug={block.get("slug")}
            title={block.get("name")}
            onPropUpdate={_onPropUpdate}
          />
        </PropertyPanelSectionWrapper>
      </When>
      <PropertyPanelSectionWrapper
        title="Advanced"
        open={activeSection === PANEL_SECTION_BLOCK_ADVANCED}
        onSIPanelToggle={() =>
          _togglePanelSection(PANEL_SECTION_BLOCK_ADVANCED)
        }
      >
        <AdvancedProperties entity={block} />
      </PropertyPanelSectionWrapper>
    </>
  );
};

export default BlockProperties;
